import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import { movies, heroMovieIds } from "@/mocks/movies";
import { newsList } from "@/mocks/news";

const heroMovies = heroMovieIds
  .map((id) => movies.find((m) => m.id === id))
  .filter((m): m is NonNullable<typeof m> => Boolean(m));

const hotMovies = [...movies].sort((a, b) => b.heat - a.heat).slice(0, 10);
const latestMovies = [...movies]
  .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
  .slice(0, 6);

const quickEntries = [
  { label: "电影", desc: "院线大片", icon: "ri-film-line", to: "/category/电影" },
  { label: "电视剧", desc: "热播追剧", icon: "ri-tv-2-line", to: "/category/电视剧" },
  { label: "影视资讯", desc: "新闻影评", icon: "ri-newspaper-line", to: "/news" },
  { label: "类型筛选", desc: "多维查找", icon: "ri-price-tag-3-line", to: "/category/电影/科幻" },
];

const featuredNews = newsList.slice(0, 3);

export default function Home() {
  return (
    <div className="w-full">
      <HeroCarousel movies={heroMovies} />

      {/* Quick entries */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {quickEntries.map((entry) => (
              <Link
                key={entry.to}
                to={entry.to}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50 transition-colors group-hover:bg-primary-600">
                  <i className={`${entry.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-600 text-foreground-950">{entry.label}</p>
                  <p className="truncate text-xs text-foreground-500">{entry.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot movies */}
      <section className="mx-auto max-w-site px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          title="热门影视推荐"
          subtitle="根据观看热度为你精选的影视作品"
          moreTo="/category/电影"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Latest updates */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-site px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            title="最新影视更新"
            subtitle="按更新时间排序，第一时间掌握新上线资源"
            moreTo="/category/电视剧"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {latestMovies.map((movie) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <div className="relative h-[112px] w-[76px] shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={movie.poster}
                    alt={`《${movie.title}》封面`}
                    title={`${movie.title} 秋霞影视`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="truncate text-sm font-600 text-foreground-950 group-hover:text-primary-700">
                      {movie.title}
                    </h3>
                    <span className="flex shrink-0 items-center gap-1 text-xs font-600 text-secondary-600">
                      <i className="ri-heart-3-fill text-xs" aria-hidden="true"></i>
                      {movie.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-600">
                    {movie.genres.join(" / ")} · {movie.region} · {movie.year}
                  </p>
                  <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-500">
                    {movie.synopsis}
                  </p>
                  <div className="mt-2 flex items-center gap-1 text-xs text-accent-700">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    更新于 {movie.updatedAt}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* News */}
      <section className="mx-auto max-w-site px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          title="影视资讯精选"
          subtitle="最新影视新闻、深度影评与预告片资讯"
          moreTo="/news"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featuredNews.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
            >
              <div className="relative h-0 w-full pb-[62%] overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 秋霞影视资讯`}
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-md bg-secondary-500 px-2 py-0.5 text-xs font-500 text-background-50">
                  {item.category}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="line-clamp-2 text-sm font-600 leading-snug text-foreground-950 group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-500">
                  {item.summary}
                </p>
                <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                  <span>{item.author}</span>
                  <span>{item.date}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Brand intro */}
      <section className="bg-primary-950">
        <div className="mx-auto max-w-site px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-700 text-background-50 md:text-3xl">
                探索属于你的观影世界
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-100/75 md:text-base">
                秋霞影视汇聚海量电影与电视剧资料，覆盖科幻、悬疑、古装、都市等丰富类型。
                你可以通过多维筛选快速定位心仪作品，收藏感兴趣的内容，并随时了解最新影视动态。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/category/电影"
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-600 text-primary-950 transition-colors hover:bg-accent-400 whitespace-nowrap"
                >
                  <i className="ri-compass-3-line text-lg" aria-hidden="true"></i>
                  开始探索
                </Link>
                <Link
                  to="/about"
                  className="rounded-md border border-background-50/25 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
                >
                  了解我们
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { num: "12,000+", label: "影视作品" },
                { num: "8,600+", label: "剧集资源" },
                { num: "20+", label: "影视类型" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-50/15 bg-background-50/5 p-5 text-center"
                >
                  <p className="font-heading text-2xl font-700 text-accent-400 md:text-3xl">
                    {stat.num}
                  </p>
                  <p className="mt-1 text-xs text-background-100/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}